import { useMemo, useEffect } from 'react'
import { useLocation } from 'react-router'

import { useSetPan } from '@sketch-hq/sketch-web-renderer'
import { useStableHandler } from '@sketch/utils'

import { Coordinates, ReferenceBounds } from '../types'

interface UseDotPanCoordinatesProps {
  coordinates: Coordinates
  referenceBounds: ReferenceBounds
  prefixBounds?: Coordinates
}

const useDotPanCoordinates = ({
  coordinates,
  referenceBounds,
  prefixBounds,
}: UseDotPanCoordinatesProps) => {
  return useMemo(() => {
    const adjustmentX = referenceBounds.x - (prefixBounds?.x || 0)
    const adjustmentY = referenceBounds.y - (prefixBounds?.y || 0)

    const panCoordinates = {
      x: -(coordinates.x - adjustmentX),
      y: -(coordinates.y - adjustmentY),
    }

    return panCoordinates
  }, [
    referenceBounds.x,
    referenceBounds.y,
    prefixBounds?.x,
    prefixBounds?.y,
    coordinates.x,
    coordinates.y,
  ])
}

interface CenteredDotLocationState {
  isFromCommentTab?: boolean
}

interface UsePanDotToViewportCenterProps extends UseDotPanCoordinatesProps {
  annotationIdentifier: string
}

/**
 * __usePanDotToViewPortCenter__
 *
 * This hook pans any annotation dot *that is activated by clicking the comment
 * tab* to the center of the viewport.
 */
const usePanDotToViewportCenter = ({
  annotationIdentifier,
  coordinates,
  referenceBounds,
  prefixBounds,
}: UsePanDotToViewportCenterProps) => {
  const setPan = useSetPan()
  const location = useLocation<CenteredDotLocationState | undefined>()

  const panCoordinates = useDotPanCoordinates({
    coordinates,
    referenceBounds,
    prefixBounds,
  })

  const panToCenter = useStableHandler(() => {
    setPan({
      ...panCoordinates,
      // Centre the camera onto the annotation
      centerPointInViewport: true,
    })
  })

  useEffect(() => {
    if (location.state?.isFromCommentTab) {
      panToCenter()
    }
  }, [location.state?.isFromCommentTab, annotationIdentifier, panToCenter])
}

export default usePanDotToViewportCenter
